import { HttpContext, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { EMPTY, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { SKIP_AUTH_RETRY } from './auth-context.token';

/**
 * Auth endpoints that manage their own credentials (cookie-based or public).
 * These bypass token attachment and the refresh-and-retry cycle entirely.
 */
const AUTH_PATHS = [
  '/api/Auth/login',
  '/api/Auth/refresh',
  '/api/Auth/logout',
  '/api/Auth/recover-password',
  '/api/Auth/reset-password',
  '/api/Auth/change-password',
];

function isAuthPath(url: string): boolean {
  return AUTH_PATHS.some((p) => url.includes(p));
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);

  // Auth endpoints and already-retried requests bypass all session logic.
  if (isAuthPath(req.url) || req.context.get(SKIP_AUTH_RETRY)) {
    return next(req);
  }

  // Wait for startup hydration, ensure a usable token, attach it, then fire.
  return auth.initialized$.pipe(
    filter((initialized) => initialized),
    take(1),
    switchMap(() => auth.ensureValidToken()),
    switchMap((valid) => {
      if (!valid) {
        // Refresh already failed during ensureValidToken; session is cleared.
        auth.logout();
        return EMPTY;
      }

      const token = auth.getToken();
      const authedReq = token
        ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
        : req;

      return next(authedReq).pipe(
        catchError((err: unknown) => {
          if (err instanceof HttpErrorResponse && err.status === 401) {
            // Race condition near expiry: attempt one shared refresh and replay.
            return auth.refresh().pipe(
              switchMap((res) => {
                const retryReq = req.clone({
                  setHeaders: { Authorization: `Bearer ${res.token}` },
                  context: new HttpContext().set(SKIP_AUTH_RETRY, true),
                });
                return next(retryReq);
              }),
              catchError(() => {
                auth.logout();
                return throwError(() => err);
              }),
            );
          }
          return throwError(() => err);
        }),
      );
    }),
  );
};
